import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, X, Ban, RotateCcw } from "lucide-react";
import { api, formatApiError } from "../lib/api";

const field = "bg-ink border border-white/10 focus:border-gold outline-none px-3 py-2 text-sm text-ivory transition-colors";

export default function AdminDiscounts() {
  const [data, setData] = useState(null);

  const load = () => api.get("/admin/discounts").then(({ data }) => setData(data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, []);

  if (!data) return <p className="text-stone-mist text-sm">Loading...</p>;

  const saveScheme = async (scheme, enabled, percent) => {
    try {
      await api.put("/admin/discounts/config", { scheme, enabled, percent: parseFloat(percent) });
      toast.success("Scheme updated");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const review = async (id, action) => {
    try {
      const { data } = await api.post(`/admin/verification-requests/${id}/${action}`);
      if (action === "approve") toast.success(`Code issued: ${data.code}`);
      else toast.success("Request rejected");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const toggleRevoke = async (code) => {
    try {
      const { data } = await api.post(`/admin/discount-codes/${code}/toggle-revoke`);
      toast.success(data.revoked ? "Code revoked" : "Code re-enabled");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const codeStatus = (c) =>
    c.revoked ? "revoked" : c.redeemed_at ? "redeemed" : c.expires_at < new Date().toISOString() ? "expired" : "active";

  return (
    <div className="space-y-12" data-testid="admin-discounts">
      {!data.verification_active && (
        <p className="text-xs text-gold/90 border border-gold/25 bg-gold/5 px-4 py-3 leading-relaxed" data-testid="admin-provider-note">
          Automated partner verification (SheerID / Blue Light Card API) is not connected — requests are reviewed
          manually here and codes are issued on approval. Add provider credentials to the server environment to
          activate automated verification.
        </p>
      )}

      <div>
        <h2 className="text-xs uppercase tracking-[0.25em] text-gold mb-5 font-medium">Discount Schemes</h2>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {["blue_light", "veterans"].map((scheme) => {
            const s = data.config[scheme] || { enabled: true, percent: 10 };
            return (
              <div key={scheme} className="border border-white/10 p-5 flex flex-wrap items-center gap-4" data-testid={`scheme-admin-${scheme}`}>
                <p className="text-ivory font-light flex-1 min-w-[140px]">{scheme === "blue_light" ? "Blue Light Card" : "Veterans"}</p>
                <label className="flex items-center gap-2 text-xs text-stone-mist">
                  <input
                    type="number"
                    min="1"
                    max="50"
                    defaultValue={s.percent}
                    className={field + " w-20"}
                    data-testid={`percent-${scheme}`}
                    onChange={(e) => (s.percent = e.target.value)}
                  />
                  % off
                </label>
                <label className="flex items-center gap-2 text-xs text-stone-mist">
                  <input
                    type="checkbox"
                    defaultChecked={s.enabled}
                    className="accent-[#C5A059]"
                    data-testid={`enabled-${scheme}`}
                    onChange={(e) => (s.enabled = e.target.checked)}
                  />
                  Enabled
                </label>
                <button
                  onClick={() => saveScheme(scheme, s.enabled, s.percent)}
                  data-testid={`save-${scheme}`}
                  className="bg-gold text-ink px-4 py-2 text-xs uppercase tracking-[0.2em] font-medium hover:bg-gold-glow transition-colors"
                >
                  Save
                </button>
              </div>
            );
          })}
        </div>
      </div>

      <div>
        <h2 className="text-xs uppercase tracking-[0.25em] text-gold mb-5 font-medium">Verification Requests</h2>
        <div className="space-y-3">
          {data.requests.map((r) => (
            <div key={r.id} className="border border-white/10 p-5 flex flex-wrap items-center gap-4" data-testid={`request-${r.id.slice(0, 6)}`}>
              <div className="flex-1 min-w-[220px]">
                <p className="text-ivory text-sm">{r.name} · <span className="text-gold">{r.email}</span></p>
                <p className="text-stone-mist text-xs mt-1">
                  {r.scheme === "blue_light" ? "Blue Light Card" : "Veterans"} · Ref: {r.reference} ·{" "}
                  {new Date(r.created_at).toLocaleDateString("en-GB")}
                </p>
              </div>
              {r.status === "pending" ? (
                <div className="flex gap-2">
                  <button onClick={() => review(r.id, "approve")} data-testid={`approve-${r.id.slice(0, 6)}`} className="flex items-center gap-1.5 bg-gold text-ink px-4 py-2 text-xs uppercase tracking-[0.15em] font-medium hover:bg-gold-glow transition-colors">
                    <Check className="w-3.5 h-3.5" /> Approve & Issue Code
                  </button>
                  <button onClick={() => review(r.id, "reject")} data-testid={`reject-${r.id.slice(0, 6)}`} className="flex items-center gap-1.5 border border-white/15 text-stone-mist px-4 py-2 text-xs uppercase tracking-[0.15em] hover:border-red-400 hover:text-red-400 transition-colors">
                    <X className="w-3.5 h-3.5" /> Reject
                  </button>
                </div>
              ) : (
                <p className={`text-xs uppercase tracking-[0.2em] ${r.status === "approved" ? "text-emerald-400" : "text-stone-mist"}`}>
                  {r.status}{r.code ? ` · ${r.code}` : ""}
                </p>
              )}
            </div>
          ))}
          {data.requests.length === 0 && <p className="border border-white/5 p-8 text-center text-stone-mist text-sm">No verification requests yet.</p>}
        </div>
      </div>

      <div>
        <h2 className="text-xs uppercase tracking-[0.25em] text-gold mb-5 font-medium">Issued Codes</h2>
        <div className="border border-white/10 overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-[0.2em] text-stone-mist border-b border-white/10">
                <th className="p-4">Code</th><th className="p-4">Customer</th><th className="p-4">Scheme</th><th className="p-4">Discount</th><th className="p-4">Expires</th><th className="p-4">Status</th><th className="p-4"></th>
              </tr>
            </thead>
            <tbody>
              {data.codes.map((c) => (
                <tr key={c.code} className="border-b border-white/5" data-testid={`code-${c.code}`}>
                  <td className="p-4 font-mono text-gold">{c.code}</td>
                  <td className="p-4 text-ivory">{c.customer_name}<span className="block text-xs text-stone-mist">{c.customer_email}</span></td>
                  <td className="p-4 text-stone-mist text-xs">{c.scheme === "blue_light" ? "Blue Light" : "Veterans"}</td>
                  <td className="p-4 text-stone-mist">{c.percent}%</td>
                  <td className="p-4 text-stone-mist text-xs font-mono">{c.expires_at?.slice(0, 10)}</td>
                  <td className="p-4">
                    <span className={`text-xs uppercase tracking-[0.15em] ${
                      codeStatus(c) === "active" ? "text-emerald-400" : codeStatus(c) === "redeemed" ? "text-gold" : "text-red-400"
                    }`}>
                      {codeStatus(c)}
                    </span>
                  </td>
                  <td className="p-4 text-right">
                    {!c.redeemed_at && (
                      <button onClick={() => toggleRevoke(c.code)} data-testid={`revoke-${c.code}`} className="text-stone-mist hover:text-red-400 transition-colors" title={c.revoked ? "Re-enable" : "Revoke"}>
                        {c.revoked ? <RotateCcw className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {data.codes.length === 0 && <p className="p-8 text-center text-stone-mist text-sm">No codes issued yet.</p>}
        </div>
      </div>
    </div>
  );
}
